import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  UserCheck, 
  Scale, 
  Users,
  Pill,
  Shield,
  Building2,
  Settings,
  ArrowRight,
  CheckCircle,
  Clock,
  FileText,
  HeartHandshake,
  RefreshCw,
  Gavel
} from 'lucide-react';

// Services Data
const services = [
  {
    icon: UserCheck,
    title: 'Pre-Employment Verification',
    description: 'Comprehensive background screening before onboarding new employees. Verify identity, employment history, education, and criminal records.',
    features: ['Identity Verification', 'Employment History', 'Education Check', 'Criminal Background'],
  },
  {
    icon: RefreshCw,
    title: 'Post-Employment Verification',
    description: 'Continuous monitoring and periodic re-verification of existing workforce to ensure ongoing compliance and safety.',
    features: ['Periodic Re-verification', 'Continuous Monitoring', 'Compliance Updates', 'Risk Assessment'],
  },
  {
    icon: MapPin,
    title: 'Address Verification',
    description: 'Comprehensive address verification including permanent, current, and field-based verification. We physically verify addresses through our pan-India field network to ensure accuracy.',
    features: ['Permanent Address', 'Current Address', 'Field-Based Verification', 'Utility Bill Cross-Check'],
  },
  {
    icon: Briefcase,
    title: 'Employment Verification',
    description: 'Thorough verification of previous employment including tenure, designation, salary, and reason for leaving. Direct HR verification with documentation.',
    features: ['Employment Tenure', 'Designation Verification', 'Salary Confirmation', 'Exit Reason'],
  },
  {
    icon: GraduationCap,
    title: 'Education & Academic Verification',
    description: 'Verification of educational qualifications including degrees, certificates, and academic records directly from institutions.',
    features: ['Degree Verification', 'Certificate Authentication', 'Transcript Validation', 'Institution Confirmation'],
  },
  {
    icon: Scale,
    title: 'Criminal Record Checks',
    description: 'Thorough criminal background screening including court record verification at district, state, and central levels.',
    features: ['District Court Records', 'High Court Records', 'Criminal Database Check', 'Police Verification'],
  },
  {
    icon: Gavel,
    title: 'Civil Checks',
    description: 'Comprehensive civil litigation verification including civil court cases, property disputes, and legal proceedings history.',
    features: ['Civil Court Records', 'Property Disputes', 'Legal Proceedings', 'Litigation History'],
  },
  {
    icon: HeartHandshake,
    title: 'Matrimonial Checks',
    description: 'Background verification for marriage proposals ensuring family safety. Verify identity, family background, employment, and character.',
    features: ['Identity Verification', 'Family Background', 'Employment Status', 'Character Assessment'],
  },
  {
    icon: UserCheck,
    title: 'Identity & ID Checks',
    description: 'Comprehensive identity verification using government-issued documents including PAN, Aadhaar, DL, and Voter ID.',
    features: ['PAN Verification', 'Aadhaar Validation', 'Driving License Check', 'Voter ID Verification'],
  },
  {
    icon: Users,
    title: 'Reference Checks',
    description: 'Professional and personal reference verification to assess character, work ethic, and professional conduct.',
    features: ['Professional References', 'Manager Feedback', 'Colleague Verification', 'Character Assessment'],
  },
  {
    icon: Pill,
    title: 'Drug Testing',
    description: 'Pre-employment and periodic drug screening services in partnership with certified laboratories.',
    features: ['Pre-Employment Screening', 'Random Testing', 'Post-Incident Testing', 'Certified Lab Partners'],
  },
  {
    icon: Shield,
    title: 'Insurance Verification & Claim Checks',
    description: 'Comprehensive verification services for insurance companies including policyholder verification and claim investigation.',
    features: ['Policyholder Verification', 'Claim Investigation', 'Document Authentication', 'Field Investigation'],
  },
  {
    icon: Building2,
    title: 'Vendor & Third-Party Verification',
    description: 'Due diligence and compliance verification for vendors, contractors, and third-party partners.',
    features: ['Company Registration', 'Financial Health Check', 'Compliance Verification', 'Background Check'],
  },
  {
    icon: Settings,
    title: 'Custom & Industry-Specific Solutions',
    description: 'Tailored verification packages designed for specific industry requirements and organizational needs.',
    features: ['Custom Packages', 'Industry-Specific Checks', 'Bulk Processing', 'API Integration'],
  },
];

// TAT Data
const tatData = [
  { type: 'Pre-Employment Verification (Complete)', tat: '5-7 Working Days' },
  { type: 'Post-Employment Verification', tat: '3-5 Working Days' },
  { type: 'Address Verification (Urban)', tat: '2-3 Working Days' },
  { type: 'Address Verification (Rural)', tat: '3-5 Working Days' },
  { type: 'Employment Verification', tat: '3-5 Working Days' },
  { type: 'Education Verification', tat: '5-7 Working Days' },
  { type: 'Identity Checks (Digital)', tat: 'Instant - 24 Hours' },
  { type: 'Criminal Record Check', tat: '5-10 Working Days' },
  { type: 'Civil Checks', tat: '5-10 Working Days' },
  { type: 'Matrimonial Checks', tat: '7-10 Working Days' },
  { type: 'Reference Checks', tat: '2-4 Working Days' },
  { type: 'Drug Testing', tat: '2-3 Working Days' },
  { type: 'Insurance Verification', tat: '3-7 Working Days' },
  { type: 'Vendor Verification', tat: '5-10 Working Days' },
];

export default function Services() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-28" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="w-4 h-4" />
              Our Services
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">
              Comprehensive Verification Services
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our verification services are scalable, compliant, and customizable to meet the diverse 
              requirements of enterprises, insurers, and institutions. From identity checks to 
              comprehensive background screening, we have you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 lg:py-28 bg-white" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Core Service Offerings
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              End-to-end verification solutions designed to support informed decision-making 
              and ensure regulatory compliance
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-slate-100 card-hover">
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-7 h-7" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-slate-900 mb-2">{service.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, i) => (
                      <span 
                        key={i} 
                        className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm"
                      >
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Turnaround Time Section */}
      <section className="py-20 lg:py-28 bg-slate-50" data-testid="tat-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Turnaround Time
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Defined TAT for All Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We commit to clear turnaround times and deliver reliable results on schedule
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Card className="border-slate-200 shadow-soft">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-900 hover:bg-slate-900">
                      <TableHead className="text-white font-semibold text-base py-4">Verification Type</TableHead>
                      <TableHead className="text-white font-semibold text-base py-4 text-right">Typical TAT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tatData.map((item, index) => (
                      <TableRow key={index} className="hover:bg-slate-50">
                        <TableCell className="font-medium text-slate-900 py-4">{item.type}</TableCell>
                        <TableCell className="text-right py-4">
                          <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                            <Clock className="w-3 h-3" />
                            {item.tat}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <p className="text-sm text-slate-500 mt-4 text-center">
              * TAT may vary based on location accessibility, document availability, and verification complexity. 
              Express processing available for urgent requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Simple, streamlined process for all your verification needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '01', title: 'Submit Request', description: 'Share candidate details through our portal, API, or bulk upload' },
              { step: '02', title: 'Verification', description: 'Our team conducts thorough field and digital verification' },
              { step: '03', title: 'Report Delivery', description: 'Receive detailed, actionable reports with clear findings' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-slate-900" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Need a Custom Verification Package?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            We offer tailored solutions to meet your specific requirements. 
            Get in touch to discuss your verification needs.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-base"
              data-testid="services-contact-btn"
            >
              Request a Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
